import { useEffect, useState } from "react";
import { POLICY_HINT, checkPassword } from "../utils/passwordPolicy";

/**
 * "Generate one" or "I'll type it" — for the CREATE forms.
 *
 * Onboarding could only ever generate a password. That is the right default
 * and a poor only-option: an administrator standing next to a new starter
 * wants to hand over something the person can type once, and a random
 * 16-character string is not it — so they went to the vendor's admin centre
 * instead, which puts the change outside our audit trail.
 *
 * PasswordResetModal is the equivalent for resets; this is deliberately an
 * inline field rather than a modal, because it belongs inside the form that is
 * already open.
 *
 * The parent gets `undefined` while "generate" is selected, so it can simply
 * omit the field and let the server generate one.
 */
export default function PasswordChoice({
  value,
  onChange,
  username,
  displayName,
  disabled,
}: {
  /** The typed password, or undefined when generating. */
  value: string | undefined;
  onChange: (v: string | undefined) => void;
  /** Used to refuse a password containing the account name. */
  username?: string | null;
  displayName?: string | null;
  disabled?: boolean;
}) {
  const [mode, setMode] = useState<"auto" | "typed">("auto");
  const [confirm, setConfirm] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (mode === "auto") {
      onChange(undefined);
      setConfirm("");
      setTouched(false);
    }
    // onChange is a fresh closure each render in most callers; depending on it
    // would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const typed = value || "";
  // Only nag once they have actually typed something — a red box on an empty
  // field the moment you pick the option is just noise.
  const problem = touched && typed ? checkPassword(typed, { username, displayName }) : null;
  const mismatch = touched && confirm && typed !== confirm ? "The two do not match." : null;

  const optionClass = (active: boolean) =>
    "flex items-start gap-2 text-sm cursor-pointer p-2.5 rounded-lg border transition-colors " +
    (active
      ? "border-brand bg-brand/5"
      : "border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/40");

  return (
    <div className="space-y-2">
      <label className="block text-xs text-gray-500 dark:text-gray-400">Initial password</label>

      <label className={optionClass(mode === "auto")}>
        <input
          type="radio"
          checked={mode === "auto"}
          onChange={() => setMode("auto")}
          disabled={disabled}
          className="mt-0.5"
        />
        <div>
          <div className="font-medium text-gray-800 dark:text-gray-100">Generate a strong one</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Shown once when the account is created, so you can hand it over.
          </div>
        </div>
      </label>

      <label className={optionClass(mode === "typed")}>
        <input
          type="radio"
          checked={mode === "typed"}
          onChange={() => setMode("typed")}
          disabled={disabled}
          className="mt-0.5"
        />
        <div className="flex-1">
          <div className="font-medium text-gray-800 dark:text-gray-100">Set one now</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            For handing over in person. They'll be asked to change it at first sign-in.
          </div>

          {mode === "typed" && (
            <div className="mt-2 space-y-2">
              {/* type="text", not "password": whoever is setting this needs to
                  read back what they are about to say out loud. */}
              <input
                type="text"
                autoComplete="new-password"
                value={typed}
                disabled={disabled}
                onChange={(e) => {
                  setTouched(true);
                  onChange(e.target.value);
                }}
                placeholder="Password"
                className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm font-mono"
              />
              <input
                type="text"
                autoComplete="new-password"
                value={confirm}
                disabled={disabled}
                onChange={(e) => {
                  setTouched(true);
                  setConfirm(e.target.value);
                }}
                placeholder="Confirm"
                className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm font-mono"
              />
              {problem || mismatch ? (
                <p className="text-xs text-red-600 dark:text-red-400">{problem || mismatch}</p>
              ) : (
                <p className="text-[11px] text-gray-400 dark:text-gray-500">{POLICY_HINT}</p>
              )}
            </div>
          )}
        </div>
      </label>
    </div>
  );
}

/**
 * What the parent form should check before submitting.
 * @returns a reason to stop, or null.
 */
export function passwordChoiceProblem(
  value: string | undefined,
  opts: { username?: string | null; displayName?: string | null } = {},
): string | null {
  if (value === undefined) return null; // generating — nothing to check
  if (!value) return "Enter a password, or choose to generate one.";
  return checkPassword(value, opts);
}
